











































































































import {Component, Vue, Prop, Watch} from 'vue-property-decorator'
import KeyframeGraph from '@/components/KeyframeGraph.vue'
import {Pose, PoseTimeline} from '@/store/pose'
import {AnimatedParam} from '@/params'
import {projectStore, shotStore} from '@/store'
import {ParamStore} from '@/store/paramstore'
import {KeyframeList, Keyframe, Time, KeyframeValue} from '@/keyframe'
import {SceneShot, StillShot, TitleShot, VideoShot} from '@/store/shots'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import ShotDuration from '@/components/ShotDuration.vue'
import FollowCamTrack from '@/components/FollowCamTrack.vue'
import {undoStack} from '@/store/undo-plugin'

// Identifies a param across all available ParamStores
interface ParamId {
  storeIndex: number;
  name: string;
  fullName: string;
}

const targetFrameInterval = 1000 / 30;

@Component({
  components: {
    KeyframeGraph, VuePerfectScrollbar, ShotDuration, FollowCamTrack
  }
})
export default class Timeline extends Vue {
  @Prop({default: 'Timeline'}) title!: string
  @Prop({default: '100%'}) width!: number | string
  @Prop({default: '200'}) height!: number | string
  @Prop() shot!: StillShot | VideoShot | SceneShot | TitleShot
  @Prop() expanded!: boolean

  playing = false
  loop = null // toggles between undefined and 0 because of how v-btn-toggle works
  playbackTimerId =-1      // ID used to cancel playback timer
  scrollbarSettings = {
    suppressScrollX: true,
    wheelSpeed: 0.2
  }

  get currentParam() {
    return shotStore.selectedParam
  }

  set currentParam(paramInfo) {
    if (paramInfo.storeIndex >= 0) {
      const store = this.paramStores[paramInfo.storeIndex]
      shotStore.setSelectedParam({store, name: paramInfo.name})
    } else {
      shotStore.clearSelectedParam()
    }
  }

  get paramStores(): ParamStore[] {
    return this.shot.allParamStores
  }

  get params(): ParamId[] {
    const result: ParamId[] = []
    this.paramStores.forEach((s, storeIndex) => {
      // Find the animated params from this store and collect their
      // names along with the store index.
      const tmp: ParamId[] = Object.keys(s.params)
        .filter((name: string) => s.params[name] instanceof AnimatedParam && s.params[name].hasKeyframes())
        .map(name => {
          return {storeIndex, name, fullName: s.name + '.' + name}
        })
      result.push(...tmp)
    })

    return result
  }

  // Key for v-for
  paramKey(p: ParamId) {
    return p.storeIndex + p.name
  }

  get currentKeyframes(): KeyframeList | null {
    const params = this.paramStores[this.currentParam.storeIndex]?.params
    const kfs = params && params[this.currentParam.name]?.keyframeList
    if (kfs)
      return kfs
    else
      return null
  }

  get poseTimeline() {
    return (this.shot as SceneShot).scenePoseTimeline
  }

  // If a param is selected, run a function on its keyframe list
  withCurrentKeyframes<T>(fun: ((arg: KeyframeList) => T)): T | null {
    const keyframes = this.currentKeyframes
    return keyframes && fun(keyframes)
  }

  get currentKeyframe(): Keyframe | null {
    return this.withCurrentKeyframes(c => c.getKeyAtTime(this.localTime))
  }

  get localTime() {
    return this.shot ? projectStore.localTime - this.shot.inPoint : projectStore.localTime
  }
  get totalTime() {
    return projectStore.currentShot ? this.shot.outPoint - this.shot.inPoint : 0
  }

  get reachedOutPoint() {
    return  this.shot &&
      projectStore.localTime >= Number.parseFloat((this.shot.outPoint - 0.000001).toFixed(6))
  }

  get reachedInPoint() {
    return this.shot && projectStore.localTime <= this.shot.inPoint
  }

  get showAddKeyframeBtn() {
    return !this.currentKeyframe
  }
  get disableAddKeyframeBtn() {
    return this.currentParam.name === ''
  }
  get showRemoveKeyframeBtn() {
    return !!this.currentKeyframe
  }
  get showPlayBtn() {
    return !this.playing && !this.reachedOutPoint
  }

  get showPauseBtn() {
    return !(this.showPlayBtn || this.showReplayBtn)
  }

  get showReplayBtn() {
    return !this.playing && this.reachedOutPoint
  }

  get projectLocalTime() {
    return projectStore.localTime
  }

  hasCurrentShot() {
    return !!projectStore.currentShot
  }

  get looping() {
    return this.loop === 0
  }

  get bpSm() {
    return this.$vuetify.breakpoint.sm
  }

  get bpXs() {
    return this.$vuetify.breakpoint.xs
  }
  // Explicitly set time to given value. Pauses playback. Call this in
  // direct response to user action,.
  updateTime(t: number) {
    this.pause()
    // pass through to parent
    this.$emit('update-time', t)
  }

  // Advance current time by the given interval.
  incrementTime(dt: number) {
    this.$emit('update-time', this.localTime + dt)
  }

  goToKeyframe(keyframes: KeyframeList, index: number) {
    if (index >= 0 && index < keyframes.getNumKeyframes())
      this.updateTime(keyframes.getKeyframe(index).time)
  }

  play() {
    if (this.playing)   // already playing
      return

    this.$emit('play')

    let lastUpdateTime = new Date().getTime()
    this.playbackTimerId = window.setInterval(() => {
      const now = new Date().getTime()
      this.incrementTime((now - lastUpdateTime) / 1000)
      lastUpdateTime = now
    }, targetFrameInterval)
    this.playing = true
  }

  pause(ev?: MouseEvent) {
    if (!this.playing)  // already paused
      return

    this.$emit('pause')

    // Allow ctrl-Pause to stop anywhere
    if (ev && !ev.ctrlKey) {
      const nearestPoseTime = this.poseTimeline.getNearestPose(this.localTime)?.time
      if (nearestPoseTime !== undefined)
        this.$emit('update-time', nearestPoseTime)
    }

    window.clearInterval(this.playbackTimerId)
    this.playing = false
  }

  // Pause when playback reaches the out point
  @Watch('reachedOutPoint')
  onReachedOutPoint(newval: boolean) {
    if (newval) {
      if (!this.looping)
        this.pause()
      else if (this.playing)
        this.replay()
    }
  }

  replay() {
    this.beginning()
    this.play()
  }

  beginning() {
    this.updateTime(this.shot.inPoint)
  }

  end() {
    this.updateTime(this.shot.outPoint)
  }

  nextKeyframe() {
    this.withCurrentKeyframes(c => this.goToKeyframe(c, c.getIndexAfterTime(this.localTime)))
  }

  prevKeyframe() {
    this.withCurrentKeyframes(c => this.goToKeyframe(c, c.getIndexBeforeTime(this.localTime)))
  }

  nextPose() {
    const t = this.poseTimeline.getNextPose(this.localTime)?.time
    if (t !== undefined)
      this.updateTime(t)
  }

  prevPose() {
    const t = this.poseTimeline.getPrevPose(this.localTime)?.time
    if (t !== undefined)
      this.updateTime(t)
  }

  nextPoseOrKeyframe(ev: MouseEvent) {
    console.log('nextPose: ', ev)
    if (ev && ev.ctrlKey)
      this.nextKeyframe()
    else
      this.nextPose()
  }
  prevPoseOrKeyframe(ev: MouseEvent) {
    console.log('prevPose: ', ev)
    if (ev && ev.ctrlKey)
      this.prevKeyframe()
    else
      this.prevPose()
  }


  addKeyframe() {
    const params = this.paramStores[this.currentParam.storeIndex]
    if (params)
      params.addKeyframe(this.currentParam.name)
  }

  removeKeyframe() {
    const params = this.paramStores[this.currentParam.storeIndex]
    if (params)
      params.removeKeyframe(this.currentParam.name)
  }

  keyframeChanged({kf, value, time}: {kf: Keyframe; value: KeyframeValue; time: Time}) {
    const params = this.paramStores[this.currentParam.storeIndex]
    if (params)
      params.editKeyframe(this.currentParam.name, kf, value, time)
  }

  poseChanged({pose, time}: {pose: Pose; time: Time}) {
    shotStore.modifyPose({description: `move pose to ${time}`,
                          actionType: 'move',
                          shot: this.shot as SceneShot,
                          timelineId: 'scene',
                          timeline: this.poseTimeline,
                          paramStorePath: [],
                          mutator: (timeline: PoseTimeline) => {
                            timeline.movePose(pose, time)
                          }})
  }
  addPose({time}: {time: Time}) {
    const pose = new Pose(this.poseTimeline, time)
    undoStack.beginGroup()
    shotStore.modifyPose({description: `add pose at ${time}`,
                          actionType: 'add',
                          shot: this.shot as SceneShot,
                          timelineId: 'scene',
                          timeline: this.poseTimeline,
                          paramStorePath: [],
                          mutator: (timeline: PoseTimeline) => {
                            timeline.addPose(pose)
                          }})
    shotStore.modifyPose({description: `set keyframes for pose at ${time}`,
                          actionType: 'setKeyframes',
                          shot: this.shot as SceneShot,
                          timelineId: 'scene',
                          timeline: this.poseTimeline,
                          paramStorePath: [],
                          mutator: (timeline: PoseTimeline) => {
                            pose.setKeyframes()
                          }})
    undoStack.endGroup()
  }
  deletePose({pose}: {pose: Pose}) {
    undoStack.beginGroup()
    shotStore.modifyPose({description: `clear keyframes for pose at ${pose.time}`,
                          actionType: 'clearKeyframes',
                          shot: this.shot as SceneShot,
                          timelineId: 'scene',
                          timeline: this.poseTimeline,
                          paramStorePath: [],
                          mutator: (timeline: PoseTimeline) => {
                            pose.clearKeyframes()
                          }})
    shotStore.modifyPose({description: `delete pose at ${pose.time}`,
                          actionType: 'delete',
                          shot: this.shot as SceneShot,
                          timelineId: 'scene',
                          timeline: this.poseTimeline,
                          paramStorePath: [],
                          mutator: (timeline: PoseTimeline) => {
                            timeline.deletePose(pose.time)
                          }})
    undoStack.endGroup()
  }
}
